import React, { useCallback } from "react";
import { isEqual } from "lodash-es";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "reactstrap";
import { Checkbox, Icon } from "components";
import { useTranslate } from "hooks";
import cx from "classnames";
import type { CheckboxItemProps } from "components/_Modals/ProjectDownloadModal/ReportsCheckboxes/types";
import { areItemsEqual } from "components/_Modals/ProjectDownloadModal/ReportsCheckboxes/helpers";
import { useAuthStore } from "stores/authStore";

export const CheckboxAccordionItem = React.memo<CheckboxItemProps>(
  ({
    item,
    onToggle,
    level = 0,
    expandedAccordionIds,
    setExpandedAccordionIds,
  }) => {
    const { id, translationKey, selected, children, userVerificationRequired } =
      item;

    const { canSeePrices } = useAuthStore.use.currentUser() || {};

    const hasChildren = Boolean(children && children.length > 0);
    const translate = useTranslate();
    const toggleAccordion = useCallback(
      (toggleId: string): void => {
        setExpandedAccordionIds((prev: string[]) =>
          prev.includes(toggleId)
            ? prev.filter((a) => a !== toggleId)
            : [...prev, toggleId],
        );
      },
      [setExpandedAccordionIds],
    );

    const handleCheckboxChange = useCallback(() => {
      onToggle(id);
      setExpandedAccordionIds((prevOpen: string[]) => {
        if (!prevOpen.includes(`checkbox-on-${id}`)) {
          return [...prevOpen, `checkbox-on-${id}`];
        }
        return prevOpen;
      });
    }, [id, onToggle, setExpandedAccordionIds]);

    if (!hasChildren) {
      return (
        <div className="px-3 py-2_5">
          <div className="gap-2 font-md text-gray-700 d-flex align-items-center">
            <Checkbox
              id={`checkbox-${id}`}
              checked={selected}
              onChange={handleCheckboxChange}
              data-testid={`checkbox-on-${id}`}
              wrapperClassName={undefined}
              classNames={undefined}
              disabled={userVerificationRequired && !canSeePrices}
            />
            {translate(translationKey)}
          </div>
        </div>
      );
    }

    return (
      <Accordion open={expandedAccordionIds ?? []} toggle={toggleAccordion}>
        <AccordionItem>
          <AccordionHeader
            targetId={`checkbox-on-${id}`}
            tag="div"
            className={cx("p-2_5 mb-0", {
              "border-bottom border-bottom-1 border-bottom-solid": hasChildren,
            })}
          >
            <div className="gap-2 font-md text-gray-700 d-flex align-items-center">
              <Checkbox
                id={`checkbox-${id}`}
                checked={selected}
                onChange={handleCheckboxChange}
                data-testid={`checkbox-on-${id}`}
                wrapperClassName={undefined}
                classNames={undefined}
                disabled={userVerificationRequired && !canSeePrices}
              />
              {translate(translationKey)}
            </div>

            {hasChildren && (
              <Icon prefix="trox" iconName="chevron-down-light" />
            )}
          </AccordionHeader>

          <AccordionBody accordionId={`checkbox-on-${id}`}>
            <div className="ps-3">
              {children?.map((child) => (
                <CheckboxAccordionItem
                  key={child.id}
                  item={child}
                  onToggle={onToggle}
                  level={level + 1}
                  expandedAccordionIds={expandedAccordionIds}
                  setExpandedAccordionIds={setExpandedAccordionIds}
                />
              ))}
            </div>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    );
  },
  (prevProps, nextProps) =>
    areItemsEqual(prevProps.item, nextProps.item) &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.level === nextProps.level &&
    isEqual(prevProps.expandedAccordionIds, nextProps.expandedAccordionIds),
);
